import React, {
	useCallback,
	useMemo,
	useState,
	forwardRef,
	useImperativeHandle,
} from 'react';
import type {ArtistSearchResult} from '@deezer/next/services/api/Starmania/requests/search/types';
import type {Input} from '@tempo/core';
import {HStack, ListItem, Skeleton, Text} from '@tempo/core';
import Thumbnail from '@components/Thumbnail';
import {ContentType} from '@deezer/next/services/types/contentType';
import SearchInput from '@components/SearchInput';
import {useSearch} from '@deezer/next/shared/hooks/useSearch';
import {useTranslation} from 'next-i18next';

type InputProps = React.ComponentProps<typeof Input>;
type ArtistSearchInputProps = {
	selectedArtist?: ArtistSearchResult;
	displayArtistId?: boolean;
	onSelectedArtistChange: (item?: ArtistSearchResult) => void;
	filterArtistIds?: number[];
	allowArtistUnknown?: boolean;
} & InputProps;

export type ArtistSearchInputRef = {
	setValue: (value: string) => void;
};

const ArtistSearchInput = forwardRef<
	ArtistSearchInputRef,
	ArtistSearchInputProps
>(
	(
		{
			selectedArtist,
			onSelectedArtistChange,
			filterArtistIds,
			allowArtistUnknown,
			displayArtistId,
			...rest
		},
		ref,
	) => {
		const [value, setValue] = useState(selectedArtist?.name || '');
		const {t} = useTranslation();
		const {
			isSearching,
			results,
			error,
			search: searchArtist,
			setResults,
		} = useSearch(ContentType.Artist);

		useImperativeHandle(ref, () => ({
			setValue: (value: string) => setValue(value),
		}));

		const handleChange: React.ChangeEventHandler<
			HTMLInputElement
		> = async e => {
			e.preventDefault();
			const searchValue = e.target.value;
			setValue(searchValue);
			onSelectedArtistChange(undefined);

			if (searchValue) {
				await searchArtist(searchValue);
			} else {
				setResults(undefined);
			}
		};

		const handleResultClick = useCallback(
			(item: ArtistSearchResult) => {
				onSelectedArtistChange(item);
				setValue(item.name);
			},
			[onSelectedArtistChange],
		);

		const handleUnknownArtistResultClick = useCallback(
			(value: string) => {
				setResults(undefined);
				onSelectedArtistChange({name: value, id: 0, picture: ''});
			},
			[onSelectedArtistChange, setResults],
		);

		const handleSearchClear = useCallback(() => {
			setResults(undefined);
			setValue('');
			onSelectedArtistChange(undefined);
		}, [onSelectedArtistChange, setResults]);

		const renderSearchResult = useCallback(
			({name, picture, id}: any) => (
				<HStack w="100%" top="50px" left="0px" spacing={2}>
					<Thumbnail
						coverLink={false}
						type={ContentType.Artist}
						height={'32px'}
						width={'32px'}
						picture={picture}
					/>
					<Text variant="body.default.regular">{name}</Text>
					{displayArtistId && <Text variant="body.default.regular">{id}</Text>}
				</HStack>
			),
			[displayArtistId],
		);

		const renderSearchSkeleton = useCallback(
			() => (
				<HStack spacing={2}>
					<Skeleton height={'32px'} width={'32px'} borderRadius={'base'} />
					<Skeleton height={'16px'} width={'100%'} />
				</HStack>
			),
			[],
		);

		const renderSearchPlaceholder = useCallback(() => {
			if (allowArtistUnknown) {
				return (
					<ListItem
						tabIndex={0}
						_hover={{bgColor: 'background.neutral.secondary.hovered'}}
						_focusVisible={{
							bgColor: 'background.neutral.secondary.hovered',
							outline: 'none',
						}}
						py={1}
						px={3}
						cursor="pointer"
						onClick={() => handleUnknownArtistResultClick(value)}
						borderRadius="4px"
					>
						<Text variant="body.default.regular" py={2} px={4}>
							{value}
						</Text>
					</ListItem>
				);
			} else
				return (
					<Text textAlign="center" variant="body.default.regular" py={2} px={4}>
						{t('errormessage_text_noartistmatch_d4c-web')}
					</Text>
				);
		}, [allowArtistUnknown, handleUnknownArtistResultClick, value, t]);

		const renderSearchError = useCallback(
			() => (
				<Text textAlign="center" variant="body.default.regular" py={2} px={4}>
					{t('errormessage_text_errorartistsearch_d4c-web')}
				</Text>
			),
			[t],
		);

		const filteredResults = useMemo(() => {
			if (!results || !filterArtistIds) {
				return results;
			}

			return results.filter(({id}) => !filterArtistIds.includes(id));
		}, [results, filterArtistIds]);

		const inputLeftElement = useMemo(() => {
			if (!allowArtistUnknown) {
				return (
					<Thumbnail
						coverLink={false}
						type={ContentType.Artist}
						height={'32px'}
						width={'32px'}
						picture={selectedArtist?.picture}
					/>
				);
			}
		}, [allowArtistUnknown, selectedArtist]);

		return (
			<SearchInput
				value={value}
				error={error}
				onChange={handleChange}
				searchResults={filteredResults}
				isLoading={isSearching}
				renderSearchResult={renderSearchResult}
				renderSearchSkeleton={renderSearchSkeleton}
				renderSearchPlaceholder={renderSearchPlaceholder}
				renderSearchError={renderSearchError}
				searchResultKeyExtractor={({id}) => String(id)}
				onSelectResult={handleResultClick}
				autoComplete="off"
				inputLeftElement={inputLeftElement}
				inputLeftElementProps={
					allowArtistUnknown
						? {}
						: {
								mx: 'spacing.s',
								width: 'size.m',
							}
				}
				onSearchClear={handleSearchClear}
				{...rest}
			/>
		);
	},
);

ArtistSearchInput.displayName = 'ArtistSearchInput';

export default ArtistSearchInput;
